import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  BiBriefcase,
  BiUser,
  BiDollar,
  BiBuilding,
  BiHistory,
  BiLineChart,
  BiCheckShield,
  BiTimer,
  BiTransfer, // Added icon for Transactions
} from "react-icons/bi";
import { API_BASE_URL } from "../utils/urls.js";
import { Link } from "react-router-dom";

// Stat card with clickable link
const StatCard = ({ icon, label, value, colorClass = "text-gray-500", linkTo }) => {
  const content = (
    <div className="bg-white p-4 sm:p-6 rounded-xl shadow-sm flex items-center space-x-4 h-full w-full">
      {React.cloneElement(icon, {
        className: `w-8 h-8 sm:w-10 sm:h-10 ${colorClass}`,
      })}
      <div>
        <p className="text-gray-500 text-xs sm:text-base">{label}</p>
        {/* Adjusted text size for potentially longer values */}
        <p className="text-lg sm:text-xl font-semibold text-gray-900 break-words">
          {value}
        </p>
      </div>
    </div>
  );

  return linkTo ? (
    <Link
      to={linkTo}
      className="block transition-transform transform hover:-translate-y-1 hover:shadow-lg rounded-xl"
    >
      {content}
    </Link>
  ) : (
    content // Render directly if no linkTo is provided
  );
};


const SuperAdminDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    totalOrganizations: 0,
    totalUsers: 0,
    totalPayments: 0,
    totalCases: 0,
    totalRevenue: 0,
    activeSubscriptions: 0,
    trialingOrgs: 0,
  });
  const [recentActivity, setRecentActivity] = useState([]);
  const [organizations, setOrganizations] = useState([]);

    const formatLabel = (value) => {
  if (!value) return "";
  return value
    .toString()
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

  useEffect(() => {
    const fetchData = async () => {
      const token = localStorage.getItem("token");
      if (!token) {
        setError("Authentication token not found.");
        setLoading(false);
        return;
      }

      try {
        const res = await axios.get(`${API_BASE_URL}/super-admin/dashboard/reports`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        console.log("Super Admin Dashboard Data:", res.data); // Debug log

        const { stats: basicStats, recentActivity, organizations } = res.data;

        const totalRevenue = organizations.reduce(
          (acc, org) => acc + parseFloat(org.total_revenue || 0),
          0
        );
        const activeSubscriptions = organizations.filter(
          (org) => org.billing_status === "active"
        ).length;
        const trialingOrgs = organizations.filter(
          (org) => org.billing_status === "trialing"
        ).length;

        setStats({
          totalOrganizations: basicStats.totalOrganizations,
          totalUsers: basicStats.totalUsers,
          totalPayments: basicStats.totalPayments, // Keep using totalPayments from basicStats
          totalCases: basicStats.totalCases,
          totalRevenue,
          activeSubscriptions,
          trialingOrgs,
        });

        setRecentActivity(recentActivity || []);
        setOrganizations(organizations || []);
      } catch (err) {
        console.error("Error fetching dashboard data:", err);
        setError("Failed to load dashboard data. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const getStatusBadgeClass = (status) => {
    // Return appropriate Tailwind classes based on status string
    switch (status?.toLowerCase()) { // Added optional chaining and lowercasing
        case "succeeded": // Assuming 'succeeded' is a possible status
        case "served":
            return "bg-green-100 text-green-800";
        case "failed":
            return "bg-red-100 text-red-800";
        case "pending": // Added pending status
        case "attempted":
             return "bg-yellow-100 text-yellow-800";
        case "rescheduled":
            return "bg-blue-100 text-blue-800";
        default:
            return "bg-gray-100 text-gray-600"; // Default style
    }
};


  const formatCurrency = (amount) =>
    new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(amount || 0);

  if (loading)
    return (
      <div className="flex items-center justify-center h-64">
        <p>Loading dashboard...</p>
      </div>
    );

  if (error)
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-red-500">{error}</p>
      </div>
    );

  return (
    <div className="space-y-6 p-3 sm:p-6">
      <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 text-center sm:text-left">
        Super Admin Dashboard
      </h1>

      {/* Responsive Stats Grid */}
      <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
        <StatCard icon={<BiBuilding />} label="Total Organizations" value={stats.totalOrganizations} colorClass="text-indigo-500" linkTo="/organizations" />
        <StatCard icon={<BiBriefcase />} label="Total Cases" value={stats.totalCases} colorClass="text-blue-500" linkTo="/super-admin/cases" />
        <StatCard icon={<BiUser />} label="Total Users" value={stats.totalUsers} colorClass="text-green-500" linkTo="/super-admin/users" />
        {/* Updated Total Payments card */}
        <StatCard icon={<BiDollar />} label="Succeeded Payments" value={stats.totalPayments} colorClass="text-purple-500" linkTo="/super-admin/transactions" />
        <StatCard icon={<BiLineChart />} label="Total Revenue" value={formatCurrency(stats.totalRevenue)} colorClass="text-green-600" linkTo="/super-admin/transactions" />
        <StatCard icon={<BiCheckShield />} label="Active Subscriptions" value={stats.activeSubscriptions} colorClass="text-teal-500" linkTo="/organizations" />
        <StatCard icon={<BiTimer />} label="Trialing Orgs" value={stats.trialingOrgs} colorClass="text-orange-500" linkTo="/organizations" />
        {/* NEW Transaction Card */}
        <StatCard icon={<BiTransfer />} label="Total Transactions" value="View Log" colorClass="text-gray-600" linkTo="/super-admin/transactions" />
      </div>

      {/* Responsive Organization Subscriptions Table */}
      <div className="bg-white p-4 sm:p-6 rounded-xl shadow-sm">
        <h2 className="text-lg sm:text-xl font-bold mb-4 text-gray-800 text-center sm:text-left">
          Organization Subscriptions
        </h2>
        {/* Mobile View */}
        <div className="block md:hidden space-y-3">
          {organizations.map((org) => (
            <div key={org.id} className="border rounded-lg p-3 shadow-sm">
              <p className="font-semibold text-gray-800">
                  <Link to={`/organizations/view/${org.id}`} className="text-blue-600 hover:underline">{org.name}</Link>
              </p>
              <p className="text-xs text-gray-500">Plan: <span className="capitalize">{org.plan_type}</span></p>
              <p className="text-xs text-gray-500 capitalize">Status: {formatLabel(org.billing_status)}</p>
              <p className="text-xs text-gray-500">Users: {org.total_users}</p>
              <p className="text-xs text-gray-500">
                Trial End: {org.trial_ends_at ? new Date(org.trial_ends_at).toLocaleDateString() : "N/A"}
              </p>
              <p className="text-xs text-gray-500">
                Sub End: {org.subscription_ends_at ? new Date(org.subscription_ends_at).toLocaleDateString() : "N/A"}
              </p>
            </div>
          ))}
        </div>

        {/* Desktop Table View */}
        <div className="hidden md:block overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                {["Organization", "Plan", "Status", "Users", "Trial End", "Subscription End"].map((header) => (
                  <th key={header} className="px-6 py-3 text-left font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {organizations.map((org) => ( // Changed variable name to org
                <tr key={org.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-gray-900 font-medium">
                    <Link to={`/organizations/view/${org.id}`} className="text-blue-600 hover:text-blue-900 break-words">
                      {org.name}
                    </Link>
                  </td>
                  <td className="px-6 py-4 text-gray-500 capitalize">{org.plan_type}</td>
                  <td className="px-6 py-4 text-gray-500 capitalize">{formatLabel(org.billing_status)}</td>
                  <td className="px-6 py-4 text-gray-500">{org.total_users}</td>
                  <td className="px-6 py-4 text-gray-500">
                    {org.trial_ends_at ? new Date(org.trial_ends_at).toLocaleDateString() : "N/A"}
                  </td>
                  <td className="px-6 py-4 text-gray-500">
                    {org.subscription_ends_at ? new Date(org.subscription_ends_at).toLocaleDateString() : "N/A"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Recent System Activity */}
      <div className="bg-white p-4 sm:p-6 rounded-xl shadow-sm">
        <h2 className="text-lg sm:text-xl font-bold mb-4 text-gray-800 flex items-center justify-center sm:justify-start space-x-2">
          <BiHistory />
          <span>Recent System Activity</span>
        </h2>
        <ul className="divide-y divide-gray-200">
          {recentActivity.length > 0 ? (
            recentActivity.map((log) => (
              <li key={log.id} className="py-3 flex flex-col sm:flex-row justify-between items-start sm:items-center">
                <div className="flex-1 w-full">
                  <p className="text-sm sm:text-base font-medium text-gray-900">{log.description}</p>
                  <p className="text-xs text-gray-500">
                    By: {log.first_name} {log.last_name}
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center space-y-1 sm:space-y-0 sm:space-x-4 mt-2 sm:mt-0">
                  <span
                    className={`px-2 py-1 inline-flex text-xs sm:text-sm font-semibold rounded-full capitalize ${getStatusBadgeClass(
                      log.attempt_status
                    )}`}
                  >
                    {log.attempt_status || 'N/A'} {/* Added fallback */}
                  </span>
                  <span className="text-xs text-gray-400">{new Date(log.created_at).toLocaleString()}</span>
                </div>
              </li>
            ))
          ) : (
            <p className="text-gray-500 text-center">No recent activity found.</p>
          )}
        </ul>
      </div>
    </div>
  );
};

export default SuperAdminDashboard;